import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';

const INSTAGRAM_POSTS = [
  // Dr. Venkat's Instagram Reels
  { url: 'https://www.instagram.com/reel/drvenkatsubramaniam/', handle: '@drvenkatsubramaniam', label: 'Dr. Venkat' },
  { url: 'https://www.instagram.com/p/drvenkatsubramaniam/', handle: '@drvenkatsubramaniam', label: 'Dr. Venkat' },
  // Sakthi Hospital Reels
  { url: 'https://www.instagram.com/reel/sakthi_hospital/', handle: '@sakthi_hospital', label: 'Sakthi Hospital' },
  { url: 'https://www.instagram.com/p/sakthi_hospital/', handle: '@sakthi_hospital', label: 'Sakthi Hospital' },
];

const InstagramSection = () => {
  const { language } = useLanguage();
  const sectionRef = useRef(null);

  useEffect(() => {
    // Load Instagram embed script only when section is visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (!document.getElementById('instagram-embed-script')) {
            const script = document.createElement('script');
            script.id = 'instagram-embed-script';
            script.src = 'https://www.instagram.com/embed.js';
            script.async = true;
            document.body.appendChild(script);
          } else if (window.instgrm) {
            window.instgrm.Embeds.process();
          }
          observer.disconnect();
        }
      });
    }, { threshold: 0.1 });

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <section id="instagram" className="py-14 bg-[#f8fafc]" data-testid="instagram-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1.5 bg-gradient-to-r from-[#833AB4]/10 via-[#FD1D1D]/10 to-[#FCAF45]/10 text-[#833AB4] rounded-full text-xs font-semibold uppercase tracking-wider mb-3">
            {language === 'ta' ? 'எங்கள் வீடியோக்கள்' : 'Our Videos'}
          </span>
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#0f172a] tracking-tight">
            {language === 'ta' ? 'Instagram-ல் எங்களைப் பின்தொடருங்கள்' : 'Follow Us on Instagram'}
          </h2>
          <p className="text-sm text-[#64748b] mt-2">
            {language === 'ta' ? 'எங்கள் மருத்துவர்கள் & மருத்துவமனை பற்றிய சமீபத்திய வீடியோக்கள்' : 'Latest health tips, patient stories & hospital updates'}
          </p>
        </div>

        {/* Instagram Embeds Grid */}
        <div ref={sectionRef} className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Dr. Venkat's Profile Embed */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden p-4" data-testid="instagram-drvenkat">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] flex items-center justify-center">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                  <span className="text-xs font-bold text-[#0f172a]">DV</span>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-bold text-[#0f172a]">Dr. Venkatsubramaniam</h3>
                <p className="text-xs text-[#64748b]">@drvenkatsubramaniam • Senior Urologist</p>
              </div>
            </div>
            <blockquote
              className="instagram-media"
              data-instgrm-permalink="https://www.instagram.com/drvenkatsubramaniam/"
              data-instgrm-version="14"
              style={{ background: '#FFF', border: 0, borderRadius: '12px', margin: '0 auto', maxWidth: '100%', minWidth: '280px', padding: 0, width: '100%' }}
            >
            </blockquote>
          </div>

          {/* Sakthi Hospital Profile Embed */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden p-4" data-testid="instagram-sakthihospital">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] flex items-center justify-center">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                  <span className="text-xs font-bold text-[#0f172a]">SH</span>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-bold text-[#0f172a]">Sakthi Hospital</h3>
                <p className="text-xs text-[#64748b]">@sakthi_hospital • Official Page</p>
              </div>
            </div>
            <blockquote
              className="instagram-media"
              data-instgrm-permalink="https://www.instagram.com/sakthi_hospital/"
              data-instgrm-version="14"
              style={{ background: '#FFF', border: 0, borderRadius: '12px', margin: '0 auto', maxWidth: '100%', minWidth: '280px', padding: 0, width: '100%' }}
            >
            </blockquote>
          </div>
        </div>

        {/* Follow buttons */}
        <div className="flex flex-wrap justify-center gap-4">
          <a
            href="https://www.instagram.com/drvenkatsubramaniam"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] text-white rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity shadow-md"
            data-testid="instagram-follow-drvenkat"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            Follow Dr. Venkat
          </a>
          <a
            href="https://www.instagram.com/sakthi_hospital"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] text-white rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity shadow-md"
            data-testid="instagram-follow-hospital"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            Follow Sakthi Hospital
          </a>
        </div>
      </div>
    </section>
  );
};

export default InstagramSection;
